import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { HOME_BY_ROLE } from "@/components/ProtectedRoute";
import { User, Building2, Check } from "lucide-react";
import { toast } from "sonner";

export default function ChooseRole() {
  const { user, booting, completeProfile } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("customer");
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(false);

  if (booting) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "customer" || user.role === "travel_agent") return <Navigate to={HOME_BY_ROLE[user.role]} replace />;

  const submit = async () => {
    setLoading(true);
    try {
      const u = await completeProfile(role, role === "travel_agent" ? company : undefined);
      toast.success("You're all set!");
      navigate(HOME_BY_ROLE[u.role] || "/app/home");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save."); }
    finally { setLoading(false); }
  };

  const Card = ({ value, icon: Icon, title, desc }) => (
    <button type="button" onClick={() => setRole(value)} data-testid={`role-${value}`}
      className={`relative flex-1 rounded-2xl border-2 p-5 text-left transition ${role === value ? "border-indigo-500 bg-indigo-50/60" : "border-slate-200 hover:border-slate-300"}`}>
      {role === value && <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-indigo-500 text-white"><Check className="h-3 w-3" /></span>}
      <Icon className={`h-7 w-7 ${role === value ? "text-indigo-600" : "text-slate-400"}`} />
      <p className="mt-3 font-semibold text-slate-800">{title}</p>
      <p className="mt-1 text-xs text-slate-500">{desc}</p>
    </button>
  );

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-lg rounded-2xl bg-white p-8 shadow-xl" data-testid="choose-role-page">
        <Logo variant="lg" />
        <h1 className="mt-6 font-brand text-2xl font-bold text-slate-800">Welcome, {user.name?.split(" ")[0]}!</h1>
        <p className="mt-1 text-sm text-slate-500">How will you use Suffle Online? You can pick one to get started.</p>
        <div className="mt-5 flex flex-col gap-3 sm:flex-row">
          <Card value="customer" icon={User} title="Personal" desc="Shop and discover products from verified sellers, with Suffle AI to help you find the right thing." />
          <Card value="travel_agent" icon={Building2} title="Seller" desc="Sell on Suffle — list products, manage orders and grow your store with AI tools." />
        </div>
        {role === "travel_agent" && (
          <div className="mt-4">
            <label className="text-sm font-medium text-slate-600">Store name (optional)</label>
            <input value={company} onChange={(e) => setCompany(e.target.value)} data-testid="role-company"
              className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-400" placeholder="e.g. My Store" />
          </div>
        )}
        <button onClick={submit} disabled={loading} data-testid="role-continue"
          className="mt-6 w-full rounded-xl brand-gradient-bg py-3 font-semibold text-white disabled:opacity-50">
          {loading ? "Setting up..." : "Continue"}
        </button>
      </motion.div>
    </div>
  );
}
